import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import { api, useAuth, formatApiError } from "@/auth/AuthContext";

const Field = ({ label, type, value, onChange, testId, autoComplete }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A4A4A]">{label}</label>
    <input
      type={type}
      value={value}
      required
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="mt-1.5 w-full px-4 py-3.5 bg-white border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm"
    />
  </div>
);

export default function B2BLogin() {
  const [mode, setMode] = useState("signin"); // signin | forgot | sent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const submitSignIn = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const user = await login(email.trim(), password);
      const next = params.get("next");
      navigate(next || (user.role === "super_admin" ? "/admin" : user.role === "customer" ? "/my-journeys" : "/portal"));
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setMode("sent");
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 font-body text-[#121212]">
      <SEO title="B2B Sign In — The China Explorer" description="Sign in to access your B2B travel account." path="/login" />

      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0E2A5C] text-white p-14 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15 bg-[radial-gradient(circle_at_80%_20%,#D4AF37_0%,transparent_55%)] pointer-events-none" />
        <Link to="/" className="relative font-heading text-2xl tracking-tight">The China Explorer</Link>
        <div className="relative">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">✦ B2B Travel Platform</p>
          <h1 className="font-heading text-4xl xl:text-5xl mt-4 leading-tight">Your Gateway<br/>to China.</h1>
          <p className="mt-5 text-white/75 max-w-md leading-relaxed">
            Global Flights, China travel expertise and corporate travel services —
            for approved Travel Partners and Corporate Clients.
          </p>
        </div>
        <p className="relative text-xs uppercase tracking-[0.25em] text-[#D4AF37]/90">China is our specialty. The world is within reach.</p>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center bg-[#F7F5F0] px-6 py-16">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden block font-heading text-xl text-[#0E2A5C] mb-8">The China Explorer</Link>

          {mode === "signin" && (
            <form onSubmit={submitSignIn} className="bg-white border border-[#EAE6DF] p-8 md:p-10" data-testid="b2b-login-form">
              <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold">✦ B2B Access</p>
              <h2 className="font-heading text-3xl mt-3 text-[#0E2A5C]">Welcome to The China Explorer</h2>
              <p className="mt-2 text-sm text-[#4A4A4A]">Sign in to access your B2B travel account.</p>

              <div className="mt-8 grid gap-5">
                <Field label="Business Email" type="email" value={email} onChange={setEmail} testId="login-email" autoComplete="email" />
                <Field label="Password" type="password" value={password} onChange={setPassword} testId="login-password" autoComplete="current-password" />
              </div>

              {error && <p className="mt-4 text-sm text-[#C62828]" data-testid="login-error">{error}</p>}

              <button type="submit" disabled={loading} data-testid="login-submit"
                className="mt-7 w-full h-[52px] bg-[#C62828] hover:bg-[#B71C1C] disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2 transition-colors">
                {loading ? <Loader2 size={16} className="animate-spin"/> : <>Sign In <ArrowRight size={14}/></>}
              </button>

              <button type="button" onClick={() => { setMode("forgot"); setError(null); }} data-testid="forgot-password-link"
                className="mt-5 text-xs uppercase tracking-[0.2em] font-semibold text-[#0E2A5C] hover:text-[#C62828] transition-colors">
                Forgot Password?
              </button>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={submitForgot} className="bg-white border border-[#EAE6DF] p-8 md:p-10" data-testid="forgot-form">
              <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold">✦ Password Reset</p>
              <h2 className="font-heading text-3xl mt-3 text-[#0E2A5C]">Reset your password</h2>
              <p className="mt-2 text-sm text-[#4A4A4A]">Enter your business email and we&apos;ll send you a secure reset link.</p>
              <div className="mt-8">
                <Field label="Business Email" type="email" value={email} onChange={setEmail} testId="forgot-email" autoComplete="email" />
              </div>
              {error && <p className="mt-4 text-sm text-[#C62828]">{error}</p>}
              <button type="submit" disabled={loading} data-testid="forgot-submit"
                className="mt-7 w-full h-[52px] bg-[#0E2A5C] hover:bg-[#0A1F44] disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2 transition-colors">
                {loading ? <Loader2 size={16} className="animate-spin"/> : <>Send Reset Link <ArrowRight size={14}/></>}
              </button>
              <button type="button" onClick={() => { setMode("signin"); setError(null); }}
                className="mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-[#4A4A4A] hover:text-[#0E2A5C]">
                <ArrowLeft size={12}/> Back to sign in
              </button>
            </form>
          )}

          {mode === "sent" && (
            <div className="bg-white border border-[#EAE6DF] p-8 md:p-10 text-center" data-testid="forgot-sent">
              <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold">✦ Check your inbox</p>
              <h2 className="font-heading text-2xl mt-3 text-[#0E2A5C]">Reset link on its way</h2>
              <p className="mt-3 text-sm text-[#4A4A4A] leading-relaxed">
                If an account exists for <strong>{email}</strong>, a secure reset link has been sent. It expires in 60 minutes.
              </p>
              <button type="button" onClick={() => setMode("signin")}
                className="mt-6 text-xs uppercase tracking-[0.2em] font-semibold text-[#0E2A5C] hover:text-[#C62828]">
                Back to sign in
              </button>
            </div>
          )}

          <p className="mt-6 text-center text-xs text-[#4A4A4A]">
            Access is by invitation for approved Travel Partners and Corporate Clients.{" "}
            <Link to="/#contact" className="text-[#0E2A5C] font-semibold hover:text-[#C62828]">Contact us</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
